// AEAD layer for Nexus E2E — the §6-certifiable symmetric primitive.
//
// ALL symmetric content encryption goes through the ozcrypto WASM core
// (AES-256-GCM only — О-ГР-1). WebCrypto is deliberately NOT used here (ТП-1).
//
// Usage:
//   const aead = await loadAead(OzCryptoFactory);   // runs oz_selftest — КР-12
//   const { ct, tag } = aead.encrypt(key32, iv12, aad, plaintext);
//   const pt = aead.decrypt(key32, iv12, aad, ct, tag);  // null on auth failure

// Wrap a loaded Emscripten module into a small encrypt/decrypt API.
function makeAead(mod) {
  const copyIn = (bytes) => {
    const p = mod._malloc(bytes.length || 1);
    if (bytes.length) mod.HEAPU8.set(bytes, p);
    return p;
  };
  return {
    // key: 32 bytes, iv: 12 bytes, aad/pt: Uint8Array. Returns {ct, tag}.
    encrypt(key, iv, aad, pt) {
      const kp = copyIn(key), ivp = copyIn(iv), ap = copyIn(aad);
      const ptp = copyIn(pt), ctp = mod._malloc(pt.length || 1), tp = mod._malloc(16);
      mod._oz_aes256gcm_encrypt(kp, ivp, ap, aad.length, ptp, pt.length, ctp, tp);
      const ct = mod.HEAPU8.slice(ctp, ctp + pt.length);
      const tag = mod.HEAPU8.slice(tp, tp + 16);
      [kp, ivp, ap, ptp, ctp, tp].forEach((p) => mod._free(p));
      return { ct, tag };
    },
    // Returns the plaintext Uint8Array, or null if the tag does not verify
    // (ФТ-5 / КР-6: no plaintext is produced on authentication failure).
    decrypt(key, iv, aad, ct, tag) {
      const kp = copyIn(key), ivp = copyIn(iv), ap = copyIn(aad);
      const ctp = copyIn(ct), tp = copyIn(tag), ptp = mod._malloc(ct.length || 1);
      const rc = mod._oz_aes256gcm_decrypt(kp, ivp, ap, aad.length, ctp, ct.length, tp, ptp);
      const pt = rc === 0 ? mod.HEAPU8.slice(ptp, ptp + ct.length) : null;
      [kp, ivp, ap, ctp, tp, ptp].forEach((p) => mod._free(p));
      return pt;
    },
  };
}

// Load the WASM core and gate on its self-test (КР-12): if the KAT fails, E2E
// MUST NOT activate. OzCryptoFactory is the MODULARIZE'd loader from ozcrypto.js.
export async function loadAead(OzCryptoFactory) {
  const mod = await OzCryptoFactory();
  if (mod._oz_selftest() !== 0) {
    throw new Error('ozcrypto self-test failed — E2E encryption disabled');
  }
  return makeAead(mod);
}
